import { langFallback$Fetch } from '~/lang/utils/langfetch'
import { IsoDateFormatter } from '~/utility/date/IsoDateFormatter'
import { csrfTokenService } from '../csrf/CsrfTokenService'
import { ReviewFormData } from './types'
import { AddReviewFetchError, AddReviewValidationError } from './AddReviewError'

// Default timeout for review submission (60 seconds - user-generated content)
const ADD_REVIEW_TIMEOUT = 60000

export class AddReviewRepositoryFetch {
  async create(
    campsiteSlug: string,
    reviewFormData: ReviewFormData,
    token: string,
    langCode: string,
  ): Promise<{ id: string }> {
    // Validate token is present - backend now requires tokens for all reviews
    if (!token) {
      throw new Error('Token is required for review submission')
    }
    const url = `/_/endpoints/submit-review/${campsiteSlug}/${token}/`
    let response: any

    // Step 1: Fetch the data
    try {
      response = await this.getResponse(
        campsiteSlug,
        reviewFormData,
        token,
        langCode,
      )
    } catch (error: any) {
      // Silently return null for abort errors - they're intentional cancellations
      if (error?.name === 'AbortError' || error?.message?.includes('aborted')) {
        return { id: '' }
      }

      // Check if this is a validation error (400 with errors object OR error field)
      if (
        error?.status === 400 &&
        (error?.data?.errors || error?.data?.error)
      ) {
        // This is a server validation error, not a fetch error
        const validationError = new AddReviewValidationError(
          url,
          campsiteSlug,
          token,
          reviewFormData,
          error.data,
          error,
        )
        // Don't log here - let the machine/store handle logging to avoid duplicates
        throw validationError
      }

      // This is a true fetch error (network, 500, etc.)
      const fetchError = new AddReviewFetchError(
        url,
        campsiteSlug,
        token,
        reviewFormData,
        error,
      )
      // Don't log here - let the machine/store handle logging to avoid duplicates
      throw fetchError
    }

    // Step 2: Validate response structure
    try {
      if (!response?.success || !response?.id) {
        throw new Error(
          response?.message || 'Missing required fields in response',
        )
      }
      return { id: response.id.toString() }
    } catch (error: any) {
      const validationError = new AddReviewValidationError(
        url,
        campsiteSlug,
        token,
        reviewFormData,
        response,
        error,
      )
      // Don't log here - let the machine/store handle logging to avoid duplicates
      throw validationError
    }
  }

  private async getResponse(
    campsiteSlug: string,
    reviewFormData: ReviewFormData,
    token: string,
    langCode: string,
  ) {
    const { csrfToken } = await csrfTokenService()
    return langFallback$Fetch(
      langCode,
      `/_/endpoints/submit-review/${campsiteSlug}/${token}/`,
      {
        method: 'POST',
        headers: {
          'X-ELDARION-AJAX': 'true',
          'X-CSRFToken': csrfToken,
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: this.getFormData(reviewFormData),
        timeout: ADD_REVIEW_TIMEOUT,
      },
    )
  }

  private getFormData(data: ReviewFormData) {
    const formData = new FormData()
    formData.append('rate_overall', data.overallRate.toString())
    formData.append('group_type', data.travellingAs.toString())
    if (data.stayedIn) {
      formData.append('unit_type', data.stayedIn)
    }
    formData.append('visit_date', this.getFormDataVisitDate(data.visitDate))
    formData.append('liked', data.liked)
    formData.append('disliked', data.disliked)
    formData.append('title', data.summary)
    formData.append('rate_location', data.rateLocation.toString())
    formData.append('rate_value', data.rateValue.toString())
    formData.append('rate_quality', data.rateAccomodation.toString())
    formData.append('rate_facilities', data.rateFacilities.toString())
    formData.append('rate_service', data.rateService.toString())
    formData.append('rate_child_friendly', data.rateChildFriendly.toString())
    formData.append('rate_clean', data.rateClean.toString())
    formData.append('rate_wifi', data.rateWifi.toString())
    data.theme.forEach((theme) => {
      formData.append('themes', theme)
    })
    formData.append('recommend', data.recommendToAFriend ? 'True' : 'False')
    formData.append('revisit', data.willYouReturn ? 'True' : 'False')
    formData.append('is_anonymous', data.anonymous ? 'on' : '')
    formData.append('private_message', data.privateMessage)
    return formData
  }

  private getFormDataVisitDate(visitDate: string) {
    const newVisitDate = new Date(visitDate)
    newVisitDate.setDate(1)
    return IsoDateFormatter.toIsoDate(newVisitDate)
  }
}
